/**
 * 「一条 provider 链」的策展卡——联网搜索（web_search）与音频转文字（audio_transcribe）
 * 共用。超管在这里做三件事：勾选哪些腿启用、给需要网关凭证组的腿填组名、上下移调链序。
 *
 * #3173 接 audio_transcribe 时把 web_search 卡逐字复制了一份（REQ-270 ①）：两张卡唯一的
 * 差别是 provider id 的类型、「哪些腿要填组名」的判据、文案与 aria 前缀——全都进了 props。
 * 状态仍归页面所有（`rows` state + `rowsRef` + `touched`），卡只负责把交互翻成
 * `setRows` / `rowsRef.current` 的更新；保存时页面读 ref，这条约定与改前一致。
 *
 * 兜底表达式 `baseRowsFrom` 只此一份：改前四处手抄，语义是「用户改过就以 ref 为准，
 * 否则以 state 为准，两者都空且从没碰过才回落到 listed 的启用行」。
 */

import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { moveIndex } from "../../lib/platform-tool-curation-view.js";
import { IsolatedDraftInput } from "./IsolatedDraftInput.js";
import { ToolCard } from "./ToolCard.js";

export type ProviderRow<TId extends string> = { provider: TId; group: string };
export type ListedProvider<TId extends string> = ProviderRow<TId> & { enabled: boolean };

/** listed 里启用的行，剥掉 `enabled` 成为可保存的 rows。 */
export function enabledRows<TId extends string>(listed: readonly ListedProvider<TId>[]): ProviderRow<TId>[] {
  return listed.filter((item) => item.enabled).map((item) => ({ provider: item.provider, group: item.group }));
}

/**
 * 勾选 / 排序时的基准行集：
 *   ref 非空 → ref（用户已经改过，草稿在 ref 里）
 *   否则 state 非空、或用户碰过（touched）→ state（可能是有意清空）
 *   否则 → listed 的启用行（未配置时默认全选那份）
 */
export function baseRowsFrom<TId extends string>(
  refRows: readonly ProviderRow<TId>[],
  prev: readonly ProviderRow<TId>[],
  touched: boolean,
  listed: readonly ListedProvider<TId>[],
): ProviderRow<TId>[] {
  if (refRows.length > 0) return [...refRows];
  if (prev.length > 0 || touched) return [...prev];
  return enabledRows(listed);
}

export interface ProviderChainCardProps<TId extends string> {
  title: string;
  tool: string;
  blurb: string;
  listed: readonly ListedProvider<TId>[];
  touched: boolean;
  setTouched: (touched: boolean) => void;
  rowsRef: MutableRefObject<ProviderRow<TId>[]>;
  setRows: Dispatch<SetStateAction<ProviderRow<TId>[]>>;
  optionLabels: Record<TId, string>;
  groupPlaceholder: Partial<Record<TId, string>>;
  groupHint: string;
  /** aria-label 前缀：`${ariaPrefix}-provider-<id>` / `${ariaPrefix}-group-<enabledIndex>`。 */
  ariaPrefix: string;
  /** 上/下移按钮 aria-label 前缀：`${moveUpLabel}-<enabledIndex>`。 */
  moveUpLabel: string;
  moveDownLabel: string;
}

export function ProviderChainCard<TId extends string>(props: ProviderChainCardProps<TId>): JSX.Element {
  const { t } = useTranslation();
  const { listed, touched, setTouched, rowsRef, setRows } = props;
  const enabledIndexOf = (provider: TId): number =>
    listed.filter((item) => item.enabled).findIndex((item) => item.provider === provider);

  const commit = (update: (base: ProviderRow<TId>[]) => ProviderRow<TId>[]): void => {
    setTouched(true);
    setRows((prev) => {
      const next = update(baseRowsFrom(rowsRef.current, prev, touched, listed));
      rowsRef.current = next;
      return next;
    });
  };

  return (
    <ToolCard
      title={props.title}
      tool={props.tool}
      blurb={props.blurb}
      enabled={listed.filter((item) => item.enabled).length}
      total={listed.length}
    >
      <p className="text-xs font-medium text-dosia-text">{t("admin.toolCuration.enabledGroup")}</p>
      <div className="space-y-2">
        {listed.map((row) => (
          <div key={row.provider} className="flex flex-wrap items-center gap-2">
            <input
              type="checkbox"
              checked={row.enabled}
              aria-label={`${props.ariaPrefix}-provider-${row.provider}`}
              onChange={(e) => {
                const checked = e.target.checked;
                commit((base) =>
                  checked
                    ? (base.some((item) => item.provider === row.provider)
                      ? base
                      : [...base, { provider: row.provider, group: row.group }])
                    : base.filter((item) => item.provider !== row.provider));
              }}
              className="rounded border-dosia-border"
            />
            <span className="text-sm">{props.optionLabels[row.provider]}</span>
            {row.enabled && (
              <IsolatedDraftInput
                initial={row.group}
                aria-label={`${props.ariaPrefix}-group-${enabledIndexOf(row.provider)}`}
                placeholder={props.groupPlaceholder[row.provider]}
                onDraft={(next) => {
                  // 组名是草稿：只写 ref，不每键 setState（避免整页重渲染）。ref 还空着
                  // 说明这是第一笔改动，此时才把 state 也同步一次，让保存按钮能感知到脏。
                  setTouched(true);
                  const prev = rowsRef.current;
                  const base = prev.length > 0 ? prev : enabledRows(listed);
                  const mapped = base.map((item) => item.provider === row.provider ? { ...item, group: next } : item);
                  const wasEmpty = prev.length === 0;
                  rowsRef.current = mapped;
                  if (wasEmpty) setRows(mapped);
                }}
                className="flex-1 min-w-[12rem]"
              />
            )}
            {row.enabled && !row.group.trim() && (
              <span className="text-xs text-dosia-warning">{props.groupHint}</span>
            )}
            {row.enabled && (
              <span className="inline-flex gap-0.5">
                <button
                  type="button"
                  aria-label={`${props.moveUpLabel}-${enabledIndexOf(row.provider)}`}
                  onClick={() => {
                    const enabledIdx = enabledIndexOf(row.provider);
                    commit((base) => moveIndex(base, enabledIdx, -1));
                  }}
                  className="px-1.5 py-0.5 rounded hover:bg-dosia-hover"
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={`${props.moveDownLabel}-${enabledIndexOf(row.provider)}`}
                  onClick={() => {
                    const enabledIdx = enabledIndexOf(row.provider);
                    commit((base) => moveIndex(base, enabledIdx, 1));
                  }}
                  className="px-1.5 py-0.5 rounded hover:bg-dosia-hover"
                >
                  ↓
                </button>
              </span>
            )}
          </div>
        ))}
      </div>
    </ToolCard>
  );
}
